import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import * as Intercom from '@/lib/intercom';
import { getAuthStatus } from '@/lib/util';
import { useAuth } from '@/state/auth';
import { useNavbar } from '@/state/navbar';
import ErrorPage from '../error-page';
import LanguageSelectorModal from '../language-selector-modal';
import { LoadComponent } from '../lazy-loader';
import FetchingMe from './fetching-me';
import RenewingToken from './renewing-token';
import SessionExpired from './session-expired';
import Unauthenticated from './unauthenticated';

const AuthorizedPage = ({ component: Component, ...rest }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isNavbarCollapsed = useNavbar(state => state.isNavbarCollapsed);

  const {
    accessToken,
    refreshToken,
    me,
    isAuditor,
    sessionStartedAt,
    isAdmin,
    isSessionExpired,
  } = useAuth(
    state => ({
      accessToken: state.accessToken,
      refreshToken: state.refreshToken,
      me: state.me,
      isAuditor: state.isAuditor,
      sessionStartedAt: state.sessionStartedAt,
      isAdmin: state.isAdmin,
      isSessionExpired: state.isSessionExpired,
    }),
    shallow,
  );

  const authStatus = getAuthStatus(
    accessToken,
    refreshToken,
    me,
    sessionStartedAt,
    isSessionExpired,
  );

  useEffect(() => {
    Intercom.pageChange();
  }, [location.pathname]);

  useEffect(() => {
    if (me != null && !me.hasAgreedWithTerms && !isAdmin())
      navigate('/terms-of-use');
  }, [me]);

  if (authStatus === 'RenewingToken')
    return <RenewingToken token={refreshToken} />;
  if (authStatus === 'SessionExpired')
    return <SessionExpired isNavbarCollapsed={isNavbarCollapsed} />;
  if (authStatus === 'Unauthenticated') return <Unauthenticated />;
  if (authStatus === 'FetchingMe') return <FetchingMe />;
  if (authStatus === 'Invalid')
    return (
      <div
        id='content'
        className={`${isNavbarCollapsed ? 'is-navbar-collapsed' : 'is-navbar-expanded'}`}
      >
        <ErrorPage
          title='Invalid Request'
          description='We cannot proceed with the request. The problem has been reported and we are working on a fix.'
          icon='fa-solid fa-ban'
        />
      </div>
    );
  return (
    <div
      id='content'
      className={`${isNavbarCollapsed ? 'is-navbar-collapsed' : 'is-navbar-expanded'}`}
    >
      <Component {...rest} fallback={<LoadComponent />} />
      {!isAdmin() &&
        !isAuditor() &&
        !me.preferences?.userLanguage?.language &&
        !!me.hasAgreedWithTerms && <LanguageSelectorModal />}
    </div>
  );
};

export default AuthorizedPage;
